import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
} from '@angular/core';
import {
  ColumnConfiguration,
  RowConfiguration,
} from '@epicuro-next/ataraxia/grid-builder';
import { BaseGrid } from '@epicuro-next/features/config-strip-new';
import { MatrixService } from '@epicuro-next/features/config-strip/utilities/materials';
import {
  CellInventory,
  DeliveryItems,
} from '@epicuro-next/state/config-strip-state';
import { Product, ScaleSets } from 'graphql-api/gql-api-interfaces';

import { getCellInventoryTotals } from '../../../shared/cell/cell-inventory';

interface RowData {
  [key: string]: number | string;
}

export const ROW_SUBHEADER_WIDTH_CSS_VARIABLE =
  '--config-strip-preview-grid-row-subheader-width';

export const COLUMN_WIDTH_DEFAULT_VALUE = '40px';

@Component({
  selector: 'epc-preview-size-grid',
  templateUrl: './preview-size-grid.component.html',
  styleUrls: ['./preview-size-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MatrixService],
  standalone: false,
})
export class PreviewSizeGridComponent
  extends BaseGrid
  implements OnInit, AfterViewInit
{
  public readonly defaultImageWidth = 100;

  public columns: ColumnConfiguration[] = [];
  public rows: RowConfiguration<RowData>[] = [];

  public columnWidth = COLUMN_WIDTH_DEFAULT_VALUE;

  @Input() public scaleSets: ScaleSets;
  @Input() public quantities: DeliveryItems;
  @Input() public products: Product[];
  @Input() public selectedScale: string;
  @Input() public sizesInventories: { [sizeId: string]: CellInventory } = {};

  public get rowHeaderWidth(): string {
    return this.thumbnailWidth ?? '3rem';
  }

  public get isMarketing(): boolean {
    return this.rows.some((row) => row?.data.hasOwnProperty('MARKETING'));
  }

  private thumbnailWidth: string | null = null;

  public get maxImageWidth(): number {
    if (!this.rowHeaderWidth) {
      return this.defaultImageWidth;
    }

    const thumbnailWidth = parseInt(
      this.rowHeaderWidth.replace(/[^\d]/g, ''),
      10,
    );

    return Math.max(this.defaultImageWidth, thumbnailWidth);
  }
  constructor(
    private matrixStore: MatrixService,
    @Inject(DOCUMENT) private document: Document,
    private readonly elementRef: ElementRef,
  ) {
    super();
  }

  public get calculatedColumnWidth() {
    return this.isMarketing ? '1fr' : `minmax(20px, ${this.columnWidth})`;
  }

  public getCellInventoryTotals(materialId: string, row: number, col: number) {
    return getCellInventoryTotals(
      this.matrixStore.materialsMatrix,
      this.sizesInventories,
    )(materialId, row, col);
  }

  public ngAfterViewInit() {
    const computedStyles = getComputedStyle(this.document.documentElement);
    const subheaderWidth = computedStyles.getPropertyValue(
      ROW_SUBHEADER_WIDTH_CSS_VARIABLE,
    );
    this.columnWidth = subheaderWidth ? subheaderWidth : this.columnWidth;

    this.setThumbnailWidthFromStyle();
  }

  public ngOnInit() {
    this.matrixStore.selectScale(this.selectedScale, this.scaleSets);
    this.matrixStore.processMaterials(
      this.scaleSets,
      this.products,
      this.quantities,
      this.differentMaterialDimensions,
    );
    this.buildGrid();
  }

  public buildGrid() {
    this.columns = this.matrixStore.xSizes.map((size) => ({
      columnName: size,
      bindProperty: size,
      columnHeaderClass: 'heading-cell',
      cellClass: 'preview-cell',
    }));
    this.rows = this.products.map((product) => {
      const productData = this.matrixStore.materialsMatrix.get(
        product.productId,
      ) ?? [[]];
      const mapProductData = (index: number) => {
        return (
          productData[index]?.reduce(
            (acc, _, productIndex) => ({
              ...acc,
              [this.matrixStore.xSizes[productIndex]]:
                productData[index][productIndex]?.quantity,
            }),
            {},
          ) ?? {}
        );
      };
      let childRows: RowConfiguration<RowData>[] = [];
      if (this.matrixStore.ySizes) {
        childRows = this.matrixStore.ySizes.map((rowTitle, index) => ({
          rowTitle,
          rowTitleClass: 'heading-cell',
          data: mapProductData(index),
        }));
      }
      return {
        data: {
          ...(this.matrixStore.ySizes ? {} : mapProductData(0)),
          image: product.mainImageUrl ?? '',
          productId: product.productId ?? '',
        },
        childRows,
      };
    });
  }

  private setThumbnailWidthFromStyle(): void {
    const computedStyle = getComputedStyle(this.elementRef.nativeElement);
    const thumbnailWidth = computedStyle
      .getPropertyValue('--thumbnail-width')
      ?.trim();
    this.thumbnailWidth = thumbnailWidth;
  }
}
